import React, { createContext, useContext, useMemo } from 'react'
import { Drawer as DrawerPrimitive } from 'vaul'

import { cn } from '../../lib/utils'

export const DRAWER_SIZE_TO_WIDTH_OR_HEIGHT: Readonly<Record<string, string>> =
  {
    xs: '320px',
    sm: '400px',
    md: '480px',
    lg: '560px',
    xl: '640px',
    full: '100%',
    fit: 'initial',
  }

export const DrawerContext = createContext<{
  customSize?: string
  direction?: 'bottom' | 'top' | 'left' | 'right'
  size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'fit'
}>({
  customSize: undefined,
  direction: 'bottom',
  size: 'md',
})

const Drawer = ({
  shouldScaleBackground = true,
  onClose,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root> & {
  customSize?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'fit'
}) => {
  const context = {
    customSize: props.customSize,
    direction: props.direction ?? 'bottom',
    size: props.size ?? 'md',
  }

  const handleClose = () => {
    document.querySelector('body')?.classList.add('drawer-closing')
    setTimeout(() => {
      onClose?.()
      setTimeout(() => {
        document.querySelector('body')?.classList.remove('drawer-closing')
      }, 250)
    })
  }

  return (
    <DrawerContext.Provider value={context}>
      <DrawerPrimitive.Root
        shouldScaleBackground={shouldScaleBackground}
        onClose={handleClose}
        {...props}
      />
    </DrawerContext.Provider>
  )
}
Drawer.displayName = 'Drawer'

const DrawerTrigger: React.FC<
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Trigger>
> = DrawerPrimitive.Trigger

const DrawerPortal = DrawerPrimitive.Portal

const DrawerClose: React.FC<
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Close>
> = DrawerPrimitive.Close

const DrawerOverlay: React.FC<
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
> = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn(
      'drawer-overlay fixed inset-0 z-50 bg-[rgba(0,0,0,0.48)]',
      className
    )}
    {...props}
  />
))
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    overlayClassname?: string
    portal?: boolean
  }
>(({ className, children, overlayClassname, portal = true, ...props }, ref) => {
  const { customSize, direction, size } = useContext(DrawerContext)

  const maxStyle = useMemo(() => {
    const maxWidth = {
      maxWidth:
        typeof customSize !== 'undefined' && customSize !== ''
          ? customSize
          : DRAWER_SIZE_TO_WIDTH_OR_HEIGHT[size],
    }

    const maxHeight = {
      height:
        typeof customSize !== 'undefined' && customSize !== ''
          ? customSize
          : DRAWER_SIZE_TO_WIDTH_OR_HEIGHT[size],
    }
    return direction === 'bottom' || direction === 'top' ? maxHeight : maxWidth
  }, [
    DRAWER_SIZE_TO_WIDTH_OR_HEIGHT,
    DRAWER_SIZE_TO_WIDTH_OR_HEIGHT[size],
    customSize,
  ])

  const content = (
    <>
      <DrawerOverlay className={overlayClassname} />
      <DrawerPrimitive.Content
        ref={ref}
        className={cn(
          'fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto flex-col rounded-t-[16px] bg-gray-50 py-2 px-6',
          className
        )}
        style={{ ...maxStyle }}
        {...props}
      >
        {children}
      </DrawerPrimitive.Content>
    </>
  )

  return portal ? <DrawerPortal>{content}</DrawerPortal> : content
})
DrawerContent.displayName = 'DrawerContent'

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex justify-center w-full pb-[38px] pt-[22px] text-gray-800 font-extrabold tracking-[1.5px] uppercase text-sm leading-5',
      className
    )}
    {...props}
  />
)
DrawerHeader.displayName = 'DrawerHeader'

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('mt-auto flex flex-col gap-2 p-4', className)}
    {...props}
  />
)
DrawerFooter.displayName = 'DrawerFooter'

const DrawerTitle: React.FC<
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
> = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
))
DrawerTitle.displayName = DrawerPrimitive.Title.displayName

const DrawerDescription: React.FC<
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
> = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
DrawerDescription.displayName = DrawerPrimitive.Description.displayName

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
}
